import { useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { updateSettings } from '../../../../actions/airDna'
import { AIRDNA_QUALITY, AIRDNA_FREQUENCY } from '../../../../constants/messages'
import { selectPropertyDetailsMap, selectSettingsMap } from '../../../../store/selectors/airDna'
import { Box, Text, Slider, SliderLabel, SliderMark, Flex, useDebounce } from '@homebotapp/hb-react-component-catalog'
import { HOMEOWNER_DIGEST_AIRDNA_MODULE } from '../../../../constants/tracking/trackingEventLocations/homeownerDigestTrackingEventLocation'

export const MSG = defineMessages({
  price: {
    id: 'AirDna.slider.pricePerNight',
    defaultMessage: 'How nice would you make it?'
  },
  val: {
    id: 'AirDna.slider.pricePerNightValue',
    defaultMessage: '${num}/night'
  },
  nights: {
    id: 'AirDna.slider.nightsPerMonth',
    defaultMessage: 'How many nights per month?'
  }
})

export interface AirDnaChartSlidersProps {
  stateKey: string
  setHasUsedShortTermRentalCalculator: (hasUsedShortTermRentalCalculator: boolean) => void
}

export const AirDnaChartSliders = ({ stateKey, setHasUsedShortTermRentalCalculator }: AirDnaChartSlidersProps) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const settings = useSelector(selectSettingsMap)[stateKey]
  const details = useSelector(selectPropertyDetailsMap)[stateKey]
  const [price, setPrice] = useState(settings.pricePerNight)
  const [nights, setNights] = useState(settings.nightsPerMonth)
  const debouncePriceUpdate = useDebounce(() => {
    dispatch(updateSettings(stateKey, { pricePerNight: price }))
  }, 500)
  const debounceNightUpdate = useDebounce(() => {
    dispatch(updateSettings(stateKey, { nightsPerMonth: nights }))
  }, 500)

  return (
    <Flex
      gap={6}
      paddingX={4}
      justify='space-between'
      marginY={8}
      direction={stateKey === 'shortTermRentalCalculator' ? ['column', null, null, 'row'] : 'column'}
    >
      <Box width={stateKey === 'shortTermRentalCalculator' ? ['100%', null, null, '43%'] : '100%'}>
        <Text id='pricePerNightLabel' textAlign='center' size='2xs'>
          {intl.formatMessage(MSG.price)}
        </Text>
        <Box h={8} />
        <Slider
          value={price}
          name='pricePerNight'
          onChange={val => {
            setPrice(val)
            setHasUsedShortTermRentalCalculator(true)
            debouncePriceUpdate()
          }}
          min={25}
          max={Math.round(details.paymentMax)}
          aria-labelledby='pricePerNightLabel'
          tracking={{
            guid: 'rVW7UKdT9dbBRfnZ8dYr2w',
            descriptor: MSG.price.defaultMessage,
            ui_context: HOMEOWNER_DIGEST_AIRDNA_MODULE
          }}
        >
          <SliderMark value={25}>{intl.formatMessage(AIRDNA_QUALITY.basic)}</SliderMark>
          <SliderLabel value={price}>
            <Text size='2xs' as='span' whiteSpace='nowrap'>
              {intl.formatMessage(MSG.val, { num: price })}
            </Text>
          </SliderLabel>
          <SliderMark whiteSpace='nowrap' value={Math.round(details.paymentMax)}>
            {intl.formatMessage(AIRDNA_QUALITY.fancy)}
          </SliderMark>
        </Slider>
      </Box>
      <Box width={stateKey === 'shortTermRentalCalculator' ? ['100%', null, null, '43%'] : '100%'}>
        <Text id='nightsPerMonthLabel' textAlign='center' size='2xs'>
          {intl.formatMessage(MSG.nights)}
        </Text>
        <Box h={8} />
        <Slider
          value={nights}
          name='nightsPerMonth'
          onChange={val => {
            setNights(val)
            setHasUsedShortTermRentalCalculator(true)
            debounceNightUpdate()
          }}
          min={1}
          max={Math.round(details.rentalNightMax)}
          aria-labelledby='nightsPerMonthLabel'
          tracking={{
            guid: '2SQaq6Y6GgDFYc4XqeVLMM',
            descriptor: MSG.nights.defaultMessage,
            ui_context: HOMEOWNER_DIGEST_AIRDNA_MODULE
          }}
        >
          <SliderMark value={1}>{intl.formatMessage(AIRDNA_FREQUENCY.seldom)}</SliderMark>
          <SliderLabel value={nights}>
            <Text size='2xs' as='span' whiteSpace='nowrap'>
              {nights}
            </Text>
          </SliderLabel>
          <SliderMark whiteSpace='nowrap' value={Math.round(details.rentalNightMax)}>
            {intl.formatMessage(AIRDNA_FREQUENCY.often)}
          </SliderMark>
        </Slider>
      </Box>
    </Flex>
  )
}
